import { omit, loadImageWithAuthorization } from '@utils'
import { RECORD_TYPE_CHECK, RECORD_TYPE_DOCUMENT } from '@constants'

const cleanupAnnotationData = data => {
    
    data.predictions.forEach((prediction, i) => {
        
        prediction.result.forEach((result, j) => {
            
            if (!result?.type?.length) {
                
                console.warn('cleanupAnnotationData invalid prediction', data.predictions[i].result[j])
                
                delete data.predictions[i].result[j]
                
            }
            
        })
        
    })
    
}

/**
 * Creates a direct URL to load an OCR image URL for LS
 * 
 * @param {String} gcsBucket GCS bucket name (e.g. "pdfs", "annotations", or "indexfiles")
 * @param {String} fileName The name of the file to fetch from GCS
 * @returns {String} Fully qualified URL to the file (pass-through the API)
 */
const createOcrUrl = (gcsBucket, fileName) => {
    
    const baseUrl = process.env.PARSE_API_BASE_URL
    const imageFileUrl = fileName.replace('.json', '.png')
    
    return `${baseUrl}/api/v1/${gcsBucket}/${imageFileUrl}`
    
}

/**
 * Formats an annotation for LS
 * 
 * @param {String} gcsBucket GCS bucket name (e.g. "pdfs", "annotations", or "indexfiles")
 * @param {String} fileName The name of the file to fetch from GCS
 * @param {String} url Fully qualified OCR image URL (@see `createOcrUrl`)
 * @param {Object} annotation The annotation to format
 * @returns {Promise<Object>} Cleaned & formatted annotation object
 */
export const formatAnnotationForLabelStudio = async (gcsBucket, fileName, url, annotation) => {
    
    // console.log('formatAnnotationForLabelStudio', annotation)
    
    const predictions = !annotation.result?.length ? [] : [{
        model_version: annotation.modelVersion,
        score: annotation.score || 1,
        result: annotation.result.map(it => {
            
            if (it.type === 'relation')
                return omit(it, [
                    'id',
                    'annotationId',
                    'createdAt',
                    'updatedAt',
                ])
            
            if (!it.value)
                return {}
            
            it.value = omit(it.value, [
                'id',
                'annotationResultId',
                'createdAt',
                'updatedAt',
            ])
            
            if (!it.value.labels)
                delete it.value.labels
            
            if (!it.value.text)
                delete it.value.text
            
            if (!it.type?.length)
                throw new Error(`Annotation result missing type: ${JSON.stringify(it)}`)
            
            it.value.x = parseFloat(it.value.x)
            it.value.y = parseFloat(it.value.y)
            it.value.width = parseFloat(it.value.width)
            it.value.height = parseFloat(it.value.height)
            it.value.rotation = parseInt(it.value.rotation, 10)
            
            return {
                id: it.id,
                type: it.type,
                from_name: it.from_name,
                to_name: it.to_name,
                origin: 'manual',
                image_rotation: it.image_rotation,
                original_height: it.original_height,
                original_width: it.original_width,
                value: it.value,
            }
            
        }),
    }]
    
    let ocrUrl = url
    
    if (!ocrUrl?.length)
        try {
            
            const { dataUrl } = await loadImageWithAuthorization(createOcrUrl(gcsBucket, fileName))
            
            ocrUrl = dataUrl
            
        } catch (e) {
            
            console.error('label-studio-data', e)
            
        }
    
    const data = {
        data: {
            ocr: ocrUrl,
        },
        annotations: [],
        predictions,
    }
    
    cleanupAnnotationData(data)
    
    // console.log('formatAnnotationForLabelStudio', data.predictions.length, 'predictions', data.predictions)
    
    return data
    
}

/**
 * Determines which labels should be shown, based on a few conditions
 * 
 * @param {Object[]} labels Core labels
 * @param {Object[]} organizationLabels Org-specific labels
 * @param {Object[]} collectionLabels Collection-specific labels
 * @param {String} recordType
 * @param {Boolean} showAllLabels If enabled, shows all available public labels (default: false)
 * @param showPrivateLabels If enabled, shows all private labels (default: false)
 * @returns {Object[]} List of labels that should be shown
 */
export const getVisibleLabels = ({
    labels,
    organizationLabels,
    collectionLabels,
    recordType,
    showAllLabels,
    showPrivateLabels,
}) => {
    
    const debugLabelsConfig = {
        labels,
        organizationLabels,
        collectionLabels,
        recordType,
        showAllLabels,
        showPrivateLabels,
    }
    
    let recordLabels = null
    let visibleLabels = [...labels]
    
    if (!showAllLabels)
        switch (recordType) {
            case RECORD_TYPE_DOCUMENT:
                recordLabels = collectionLabels
                console.log('useLabelStudio#initialize: filtering labels via collections', debugLabelsConfig)
                break
            case RECORD_TYPE_CHECK:
                recordLabels = organizationLabels
                console.log('useLabelStudio#initialize: filtering labels via organizations', debugLabelsConfig)
                break
            default:
                console.log('useLabelStudio#initialize: not filtering labels (showing all)', debugLabelsConfig)
                break
        }
    
    // Typically we want to hide private labels, but if we've
    // assigned specific labels to an org or collection, we want to override that
    if (recordLabels?.length > 0)
        visibleLabels = recordLabels
    else if (!showPrivateLabels)
        visibleLabels = visibleLabels.filter(it => it.public)
    
    return visibleLabels
    
}
